// i18n配置

export const fallbackLocale = "gb" // 兜底地区改为国际服：英国 gb
export const fallbackLng = "en-us" // 兜底语言为英语
export const defaultNS = "common"

// 支持的语言列表 - 统一维护
export const SUPPORTED_LANGUAGES = ["en-us", "zh-hant"] as const

// 支持的地区列表 - 只保留港澳台英四个区
export const regions = ["hk", "mo", "tw", "gb"] as const

// 地区默认语言映射
export const regionDefaultLanguage = {
	hk: "zh-hant",
	mo: "zh-hant",
	tw: "zh-hant",
	gb: "en-us",
} as const

// 地区支持的语言列表 - 静态配置
export const regionSupportedLanguages = {
	hk: ["zh-hant", "en-us"],
	mo: ["zh-hant", "en-us"],
	tw: ["zh-hant", "en-us"],
	gb: ["en-us"],
} as const

// 获取地区支持的语言列表
export function getRegionLanguages(region: string): string[] {
	return [
		...(regionSupportedLanguages[
			region as keyof typeof regionSupportedLanguages
		] || ["en-us"]),
	]
}

// 获取地区默认语言
export function getRegionDefaultLanguage(region: string): string {
	return (
		regionDefaultLanguage[region as keyof typeof regionDefaultLanguage] ||
		"en-us"
	)
}

// 翻译版本控制
let translationVersion: string | null = null

/**
 * 获取当前翻译版本
 */
export function getTranslationVersion(): string | null {
	return translationVersion
}

/**
 * 设置翻译版本
 */
export function setTranslationVersion(version: string): void {
	translationVersion = version
}

// i18next-http-backend 的配置
export const backendOptions = {
	loadPath: () => {
		return `${process.env.NEXT_PUBLIC_SERVER_URL}/rpc/cms/translations/public_findAll`
	},
	// parseLoadPayload 用于构建 POST 请求的 body
	parseLoadPayload: (languages: string[]) => {
		// TODO 后端接口暂不支持相关筛选
		return {}
	},

	// parse 函数处理翻译数据
	parse: (data: string, languages?: string[]) => {
		const jsonData = JSON.parse(data)

		// 从 languages 数组中获取当前语言
		let lng = fallbackLng // 默认回退语言为英语
		if (Array.isArray(languages) && languages.length > 0) {
			lng = languages[0]
		} else if (typeof languages === "string") {
			lng = languages
		}

		if (!jsonData.json) {
			return {}
		}
		// 检查并更新翻译版本信息
		if (jsonData.json.length > 0) {
			// 找出所有数据中最新的更新时间
			let latestUpdateTime = 0
			jsonData.json.forEach((item: any) => {
				if (item.updatedAt) {
					const itemTime = new Date(item.updatedAt).getTime()
					if (itemTime > latestUpdateTime) {
						latestUpdateTime = itemTime
					}
				}
			})

			// 如果找到了有效的更新时间，使用它作为版本标识
			if (latestUpdateTime > 0) {
				setTranslationVersion(latestUpdateTime.toString())
			}
		}

		return jsonData.json.reduce((acc: Record<string, string>, data: any) => {
			if (!data || !data.key) {
				return acc
			}

			function getLngValue(data: Record<string, string>, lng: string) {
				// 优先使用当前语言的翻译
				if (data[lng]) {
					return data[lng]
				}

				// 如果当前语言是zh-hant，尝试zh_hant字段
				if (lng === "zh-hant" && data["zh_hant"]) {
					return data["zh_hant"]
				}

				// 如果当前语言是en-us，尝试en_us字段
				if (lng === "en-us" && data["en_us"]) {
					return data["en_us"]
				}

				// 尝试英语兜底
				if (data["en-us"]) {
					return data["en-us"]
				}

				// 尝试其他可能的字段
				if (data["en_us"]) {
					return data["en_us"]
				}

				if (data["zh_hant"]) {
					return data["zh_hant"]
				}

				// 最后兜底返回空字符串
				return ""
			}

			acc[data.key] = getLngValue(data, lng)

			return acc
		}, {})
	},
	// 添加 customHeaders 来指定 Content-Type，确保服务器能正确处理 JSON POST body
	customHeaders: {
		"Content-Type": "application/json",
		// lang 将在运行时动态设置，不在这里硬编码
		// 默认国际服区域改为 gb
		"X-AREA": "gb",
	},
}

export function getOptions(lng = fallbackLng, ns = defaultNS) {
	return {
		supportedLngs: SUPPORTED_LANGUAGES, // 使用统一维护的语言列表
		fallbackLng: fallbackLng, // 始终使用英语作为兜底
		lng,
		fallbackNS: defaultNS,
		defaultNS,
		ns,
		// debug: true,
		lowerCaseLng: true,
	}
}
